import { useCallback, useEffect, useState } from "react";
import { format } from "date-fns";
import { TQC2080_R01Dto } from "./Dto_TQC2080_R01";
import { DateRow, useTQC2080_R01Store } from "./Store_TQC2080_R01";
import {
	getTQC2080_R01DateList,
	getTQC2080_R01Search,
	getTQC2080_R01ChartData,
	saveTQC2080_R01,
	deleteTQC2080_R01,
	downloadExcel_TQC2080_R01,
	downloadPdf_TQC2080_R01,
	downloadChartPdf_TQC2080_R01,
} from "./Api_TQC2080_R01";
import { useLoadingSpinnerStore } from "src/store/util/loadingSpinnerUtil";
import { base64ToBlob, downloadExcel } from "src/utils/blob/blobUtil";
import { showSuccess, showWarning, ShowSuccessFetch } from "src/utils/toast/toastUtil";
import useFetchActionbarAuth from "src/hooks/useFetchActionbarAuth";
import useUiuxStore from "src/store/useUiuxStore";
import { useQzPrint } from "src/hooks/useQzPrint";
import qz from "qz-tray";

/** 로컬 기준 yyyy-MM-dd */
const getLocalYyyyMmDd = (d: Date = new Date()) => format(d, "yyyy-MM-dd");

 const createBlankEntryList = (list: TQC2080_R01Dto[]): TQC2080_R01Dto[] =>
	list.map((item) => ({
		...item,
		analysisVal: 0,
		remark: "",
		supplyQty: 0,
		secondAnal: 0,
		fieldConfirm: "",
		inserted: true,
		isModified: false,
	}));

export const Content_TQC2080_R01 = () => {
	useFetchActionbarAuth("TQC2080_R01");

	const { setLoadingSpinner } = useLoadingSpinnerStore();
	const { setMessage } = useUiuxStore();
	const { printPdfFromBlob } = useQzPrint();
	const [originAnalysisDt, setOriginAnalysisDt] = useState("");
	const [originWasInserted, setOriginWasInserted] = useState(false);

	const {
		TQC2080_R01Filter,
		setTQC2080_R01Filter,
		setTQC2080_R01PastFilter,
		setDateList,
		updateDateList,
		dateRowList, //  
		removeDateList, //  
		deletedDateList, //  
		addDeletedDateList, //  
		clearDeletedDateList, //  
		TQC2080_R01List,
		setTQC2080_R01List,
		setOriginalTQC2080_R01List,
		initSelectedTQC2080_R01,
		setChartData,
		setActiveTab,
		initData,
		setScrollTop,
		setDetailScrollTop,
	} = useTQC2080_R01Store();

	// 날짜 목록 조회 (해당 연도에 일자가 없고 연도가 올해면 오늘 일자 1건으로 신규 진입)
	const fetchDateList = useCallback(
		async (year?: string) => {
			const targetYear = year || TQC2080_R01Filter.year;
			const response = await getTQC2080_R01DateList(targetYear);
			const apiList = Array.isArray(response.data.data)
				? (response.data.data as string[])
				: [];

			const now = new Date();
			const currentYear = String(now.getFullYear());
			const todayStr = getLocalYyyyMmDd(now);

			let merged = [...apiList];
			let insertedToday = false;
			if (apiList.length === 0 && targetYear === currentYear) {
				merged = [todayStr];
				insertedToday = true;
			}

			setDateList(merged);
			return { merged, insertedToday, todayStr };
		},
		[TQC2080_R01Filter.year, setDateList],
	);

	// 날짜 클릭 시 그리드 데이터 조회
	const fetchDetail = useCallback(
		async (analysisDt: string) => {
			setDetailScrollTop(null);
			setLoadingSpinner(true);
			try {
				initSelectedTQC2080_R01();
				setTQC2080_R01Filter({ searchAnalysisDt: analysisDt });
				const response = await getTQC2080_R01Search({ searchAnalysisDt: analysisDt });
				const list = response.data.data as TQC2080_R01Dto[];
				setTQC2080_R01List(list);
				setOriginalTQC2080_R01List(list.map((item) => ({ ...item })));
				ShowSuccessFetch(setMessage, `조회가 완료되었습니다. (총 ${list.length}건)`);
			} catch (error) {
				console.error("상세 조회 실패:", error);
				showWarning("데이터 조회에 실패했습니다.");
			} finally {
				setLoadingSpinner(false);
			}
		},
		[
			setLoadingSpinner,
			setTQC2080_R01Filter,
			setTQC2080_R01List,
			setOriginalTQC2080_R01List,
			setMessage,
			initSelectedTQC2080_R01,
			setDetailScrollTop,
		],
	);

	// 등록(+): 오늘 일자 기준으로 입력값이 빈 상태인 템플릿 로드
	const openBlankDetailForRegister = useCallback(
		async (analysisDt: string) => {
			setDetailScrollTop(null);
			setLoadingSpinner(true);
			try {
				initSelectedTQC2080_R01();
				setTQC2080_R01Filter({ searchAnalysisDt: analysisDt });
				const baseList =
					TQC2080_R01List.length > 0
						? TQC2080_R01List
						: ((await getTQC2080_R01Search({ searchAnalysisDt: analysisDt })).data
								.data as TQC2080_R01Dto[]);
				const blankList = createBlankEntryList(baseList);
				setTQC2080_R01List(blankList);
				setOriginalTQC2080_R01List(blankList.map((item) => ({ ...item })));
			} catch (error) {
				console.error("등록용 템플릿 조회 실패:", error);
				showWarning("등록용 데이터 준비에 실패했습니다.");
			} finally {
				setLoadingSpinner(false);
			}
		},
		[
			setLoadingSpinner,
			initSelectedTQC2080_R01,
			setTQC2080_R01Filter,
			setTQC2080_R01List,
			setOriginalTQC2080_R01List,
			TQC2080_R01List,
			setDetailScrollTop,
		],
	);

	/** 좌측 날짜 행 클릭: 저장 행은 조회, 신규(inserted) 행은 일자 비었을 때 기본일 채운 뒤 동일 조회로 우측 테이블 템플릿 표시 */
	const openDateRowDetail = useCallback(
		async (row: DateRow) => {
			setOriginAnalysisDt(row.analysisDt?.trim() ?? "");
			setOriginWasInserted(!!row.inserted);
			if (!row.inserted) {
				const dt = row.analysisDt?.trim();
				if (!dt) return;
				await fetchDetail(dt);
				return;
			}
			const today = getLocalYyyyMmDd(new Date());
			updateDateList(row.valueIndex, { ...row, analysisDt: today, inserted: true });
			await openBlankDetailForRegister(today);
		},
		[fetchDetail, openBlankDetailForRegister, updateDateList],
	);

	// 진입 시 연도별 날짜 목록 + (비어 있으면) 오늘 일자 신규 그리드
	useEffect(() => {
		void (async () => {
			const { insertedToday, todayStr } = await fetchDateList();
			if (insertedToday) {
				setOriginAnalysisDt(todayStr);
				setOriginWasInserted(true);
				await fetchDetail(todayStr);
			}
		})();
		// eslint-disable-next-line react-hooks/exhaustive-deps -- 마운트 시 1회
	}, []);

	// 조회 버튼 (연도 변경 시 날짜 목록 재조회)
	const fetchData = useCallback(async () => {
		setScrollTop(null);
		setDetailScrollTop(null);
		setLoadingSpinner(true);
		try {
			const { merged, insertedToday, todayStr } = await fetchDateList(
				TQC2080_R01Filter.year,
			);
			setTQC2080_R01PastFilter(TQC2080_R01Filter);
			clearDeletedDateList(); // 조회 시 삭제 예정 초기화
			if (insertedToday) {
				setOriginAnalysisDt(todayStr);
				setOriginWasInserted(true);
				await fetchDetail(todayStr);
			} else if (merged.length > 0) {
				// SAL2060_R01 등과 같이 조회 후 첫 행 선택 효과 — 날짜 목록은 merged[0]으로 바로 상세 조회
				setOriginAnalysisDt(merged[0]);
				setOriginWasInserted(false);
				await fetchDetail(merged[0]);
			} else {
				setOriginAnalysisDt("");
				setOriginWasInserted(false);
				initSelectedTQC2080_R01();
				setTQC2080_R01List([]);
				setTQC2080_R01Filter({ searchAnalysisDt: "" });
			}
			ShowSuccessFetch(setMessage, "날짜 목록 조회가 완료되었습니다.");
		} catch (error) {
			console.error("조회 실패:", error);
			showWarning("조회에 실패했습니다.");
		} finally {
			setLoadingSpinner(false);
		}
	}, [
		TQC2080_R01Filter,
		fetchDateList,
		fetchDetail,
		setLoadingSpinner,
		setTQC2080_R01List,
		setTQC2080_R01Filter,
		setTQC2080_R01PastFilter,
		setMessage,
		initSelectedTQC2080_R01,
		clearDeletedDateList,
		setScrollTop,
		setDetailScrollTop,
	]);

	// 차트 데이터 조회
	const fetchChartData = useCallback(async () => {
		setLoadingSpinner(true);
		try {
			const response = await getTQC2080_R01ChartData(TQC2080_R01Filter.year);
			setChartData(response.data.data as TQC2080_R01Dto[]);
		} catch (error) {
			console.error("차트 데이터 조회 실패:", error);
			showWarning("차트 데이터 조회에 실패했습니다.");
		} finally {
			setLoadingSpinner(false);
		}
	}, [TQC2080_R01Filter.year, setChartData, setLoadingSpinner]);

	const handleTabClick = useCallback(
		(tab: "journal" | "chart") => {
			setActiveTab(tab);
			if (tab === "chart") void fetchChartData();
		},
		[setActiveTab, fetchChartData],
	);

	const handleCreateClick = useCallback(async () => {
		const today = getLocalYyyyMmDd(new Date());
		setOriginAnalysisDt("");
		setOriginWasInserted(true);
		await openBlankDetailForRegister(today);
	}, [openBlankDetailForRegister]);

	const handleInfoDateChange = useCallback(
		(nextDate: Date | undefined) => {
			if (!nextDate) {
				showWarning("일자를 선택하세요.");
				return;
			}

			const prevAnalysisDt = TQC2080_R01Filter.searchAnalysisDt;
			const nextAnalysisDt = format(nextDate, "yyyy-MM-dd");

			if (!prevAnalysisDt) {
				setTQC2080_R01Filter({ searchAnalysisDt: nextAnalysisDt });
				return;
			}

			const selectedRow = dateRowList.find((r) => r.analysisDt === prevAnalysisDt);
			const duplicated = dateRowList.some(
				(r) => r.analysisDt === nextAnalysisDt && r.valueIndex !== selectedRow?.valueIndex,
			);
			if (duplicated) {
				showWarning("이미 존재하는 일자입니다.");
				return;
			}

			if (selectedRow) {
				updateDateList(selectedRow.valueIndex, {
					...selectedRow,
					analysisDt: nextAnalysisDt,
					isModified: true,
				});
			}
			setTQC2080_R01Filter({ searchAnalysisDt: nextAnalysisDt });
		},
		[TQC2080_R01Filter.searchAnalysisDt, dateRowList, setTQC2080_R01Filter, updateDateList],
	);

	//  변경: 저장 시 deletedDateList 날짜 먼저 삭제 후 Table 저장
	const handleSaveClick = useCallback(async () => {
		const hasDeleted = deletedDateList.length > 0;
		const hasSaveTarget =
			!!TQC2080_R01Filter.searchAnalysisDt && TQC2080_R01List.length > 0;

		if (!hasDeleted && !hasSaveTarget) {
			showWarning(
				TQC2080_R01Filter.searchAnalysisDt
					? "저장할 데이터가 없습니다."
					: "날짜를 선택하세요.",
			);
			return;
		}

		setLoadingSpinner(true);
		try {
			const shouldMoveExistingDate =
				!!originAnalysisDt &&
				!originWasInserted &&
				originAnalysisDt !== TQC2080_R01Filter.searchAnalysisDt &&
				!deletedDateList.includes(originAnalysisDt);

			// 1단계: 삭제 예정 날짜 API 삭제
			if (hasDeleted) {
				for (const dt of deletedDateList) {
					await deleteTQC2080_R01(dt);
				}
				clearDeletedDateList();
				showSuccess("삭제가 완료되었습니다.");
			}

			// 2단계: 현재 Table 데이터 저장
			if (hasSaveTarget) {
				if (shouldMoveExistingDate) {
					await deleteTQC2080_R01(originAnalysisDt);
				}
				await saveTQC2080_R01({
					analysisDt: TQC2080_R01Filter.searchAnalysisDt,
					dtlList: TQC2080_R01List,
				});
				showSuccess("저장이 완료되었습니다.");
				await fetchDetail(TQC2080_R01Filter.searchAnalysisDt);
				setOriginAnalysisDt(TQC2080_R01Filter.searchAnalysisDt);
				setOriginWasInserted(false);
			}

			// 날짜 목록 갱신
			await fetchDateList();
		} catch (error) {
			console.error("처리 실패:", error);
			showWarning("처리에 실패했습니다.");
		} finally {
			setLoadingSpinner(false);
		}
	}, [
		TQC2080_R01Filter,
		TQC2080_R01List,
		deletedDateList,
		setLoadingSpinner,
		clearDeletedDateList,
		fetchDateList,
		fetchDetail,
		originAnalysisDt,
		originWasInserted,
	]);

	// - 빨간행 있으면 → 복구 (토글)
	// - inserted 신규행 → 로컬 즉시 제거
	// - DB 기존행 → deletedDateList  (빨간행) + Table 초기화
	const handleDeleteClick = useCallback(() => {
		// 이미 빨간행 있으면 → 전체 복구
		if (deletedDateList.length > 0) {
			clearDeletedDateList();
			return;
		}

		if (!TQC2080_R01Filter.searchAnalysisDt) {
			showWarning("삭제할 날짜를 선택하세요.");
			return;
		}

		// inserted 신규행(DB 미저장) → 로컬에서 즉시 제거
		const selectedRow = dateRowList.find(
			(r) => r.analysisDt === TQC2080_R01Filter.searchAnalysisDt,
		);
		if (selectedRow?.inserted) {
			removeDateList(selectedRow.valueIndex);
			setOriginAnalysisDt("");
			setOriginWasInserted(false);
			initSelectedTQC2080_R01();
			setTQC2080_R01List([]);
			setTQC2080_R01Filter({ searchAnalysisDt: "" });
			setDetailScrollTop(null);
			return;
		}

		// DB 기존 날짜 → 빨간행 표시 (저장 버튼 클릭 시 실제 삭제)
		addDeletedDateList([TQC2080_R01Filter.searchAnalysisDt]);
		setOriginAnalysisDt("");
		setOriginWasInserted(false);
		initSelectedTQC2080_R01();
		setTQC2080_R01List([]);
		setTQC2080_R01Filter({ searchAnalysisDt: "" });
		setDetailScrollTop(null);
	}, [
		TQC2080_R01Filter,
		deletedDateList,
		dateRowList,
		addDeletedDateList,
		clearDeletedDateList,
		removeDateList,
		initSelectedTQC2080_R01,
		setTQC2080_R01List,
		setTQC2080_R01Filter,
		setDetailScrollTop,
	]);

	// 엑셀
	const handleExcelClick = useCallback(async () => {
		if (!TQC2080_R01Filter.searchAnalysisDt) {
			showWarning("날짜를 선택하세요.");
			return;
		}
		setLoadingSpinner(true);
		try {
			const response = await downloadExcel_TQC2080_R01({
				searchAnalysisDt: TQC2080_R01Filter.searchAnalysisDt,
			});
			const blob = base64ToBlob(
				response.data.data,
				"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
			);
			downloadExcel(blob, `액분석일지_${TQC2080_R01Filter.searchAnalysisDt}.xlsx`);
			showSuccess("엑셀 다운로드가 완료되었습니다.");
		} catch (error) {
			console.error("엑셀 다운로드 실패:", error);
			showWarning("엑셀 다운로드에 실패했습니다.");
		} finally {
			setLoadingSpinner(false);
		}
	}, [TQC2080_R01Filter, setLoadingSpinner]);

	// PDF
	const handlePdfClick = useCallback(async () => {
		if (!TQC2080_R01Filter.searchAnalysisDt) {
			showWarning("날짜를 선택하세요.");
			return;
		}
		setLoadingSpinner(true);
		try {
			const response = await downloadPdf_TQC2080_R01(TQC2080_R01Filter.searchAnalysisDt);
			const blob = new Blob([response.data], { type: "application/pdf" });
			if (qz.websocket.isActive()) {
				const ok = await printPdfFromBlob(blob, "report1");
				if (ok) {
					showSuccess("PDF가 출력되었습니다.");
				} else {
					const fileURL = URL.createObjectURL(blob);
					window.open(fileURL, "_blank");
					setTimeout(() => URL.revokeObjectURL(fileURL), 5000);
				}
			} else {
				const fileURL = URL.createObjectURL(blob);
				window.open(fileURL, "_blank");
				setTimeout(() => URL.revokeObjectURL(fileURL), 5000);
				showSuccess("PDF가 출력되었습니다.");
			}
		} catch (error) {
			console.error("PDF 출력 실패:", error);
			showWarning("PDF 출력에 실패했습니다.");
		} finally {
			setLoadingSpinner(false);
		}
	}, [TQC2080_R01Filter, printPdfFromBlob, setLoadingSpinner]);

	// 추이 차트 PDF (연도별)
	const handleChartPdfClick = useCallback(async () => {
		if (!TQC2080_R01Filter.year) {
			showWarning("연도를 선택하세요.");
			return;
		}
		setLoadingSpinner(true);
		try {
			const response = await downloadChartPdf_TQC2080_R01(TQC2080_R01Filter.year);
			const blob = new Blob([response.data], { type: "application/pdf" });
			if (qz.websocket.isActive()) {
				const ok = await printPdfFromBlob(blob, "report1");
				if (ok) {
					showSuccess("추이 차트 PDF가 출력되었습니다.");
				} else {
					const fileURL = URL.createObjectURL(blob);
					window.open(fileURL, "_blank");
					setTimeout(() => URL.revokeObjectURL(fileURL), 5000);
				}
			} else {
				const fileURL = URL.createObjectURL(blob);
				window.open(fileURL, "_blank");
				setTimeout(() => URL.revokeObjectURL(fileURL), 5000);
				showSuccess("추이 차트 PDF가 출력되었습니다.");
			}
		} catch (error) {
			console.error("추이 차트 PDF 출력 실패:", error);
			showWarning("추이 차트 PDF 출력에 실패했습니다.");
		} finally {
			setLoadingSpinner(false);
		}
	}, [TQC2080_R01Filter.year, printPdfFromBlob, setLoadingSpinner]);

	// 초기화
	const initContainerData = useCallback(() => {
		initData();
	}, [initData]);

	return {
		fetchData,
		fetchDateList,
		fetchDetail,
		openDateRowDetail,
		fetchChartData,
		handleTabClick,
		handleCreateClick,
		handleInfoDateChange,
		handleSaveClick,
		handleDeleteClick,
		handleExcelClick,
		handlePdfClick,
		handleChartPdfClick,
		initContainerData,
	};
};
